"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { viewportOnce } from "@/lib/motion";
import styles from "./ApoBankPartnerSection.module.css";

/** Pfeil-Icon aus der apoBank-Design-Vorlage. */
function Arrow() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 10h12M11 5l5 5-5 5" />
    </svg>
  );
}

// UTM-Konvention wie in ApoBankCta.tsx; utm_content kennzeichnet die
// Platzierung der Partner-Sektion (z. B. ueber-uns, ratgeber).
const UTM = "utm_source=ncrechner&utm_medium=website";

const boxes = (placement: string) => [
  {
    href: `/apo-bank/studienkredit?${UTM}&utm_campaign=Kredit&utm_content=${placement}-studienkredit-apobank`,
    tracking: "apobank_lp_studienkredit",
    // ­ = weiches Trennzeichen (&shy;), ‑ = geschützter Bindestrich
    title: "Studienkredit",
    text: "Finanzierung für Studium, PJ und Ausland",
  },
  {
    href: `/apo-bank/studierendenpaket?${UTM}&utm_campaign=Studipaket&utm_content=${placement}-studierendenpaket-apobank`,
    tracking: "apobank_lp_studierendenpaket",
    title: "Studierenden­paket",
    text: "Kostenloses Girokonto, Reiseversicherung inklusive",
  },
  {
    href: `/apo-bank/auslandskrankenversicherung?${UTM}&utm_campaign=ARKV&utm_content=${placement}-auslandskrankenversicherung-apobank`,
    tracking: "apobank_lp_auslandskrankenversicherung",
    title: "Auslands­kranken­versicherung",
    text: "Schutz bei Famulatur, PJ und Erasmus",
  },
  {
    href: `/apo-bank/facharzt-checkup?${UTM}&utm_campaign=FacharztCheckup&utm_content=${placement}-facharzt-checkup-apobank`,
    tracking: "apobank_lp_facharzt_checkup",
    title: "Facharzt Check‑up",
    text: "Finanzcheck zum Start in die Weiterbildung",
  },
];

/**
 * apoBank × ncrechner Partner-Sektion (Design-Vorlage der apoBank-Kampagne):
 * Logos, Intro und vier Boxen zu den apoBank-Landingpages unter /apo-bank/.
 * Platzierungen: Über-uns-Seite (unterhalb der Partnerliste) und Ratgeber-Übersicht.
 */
export default function ApoBankPartnerSection({
  placement = "ueber-uns",
}: {
  /** Präfix für utm_content, damit Klicks je Seite auswertbar sind. */
  placement?: string;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={styles.section}
      id="apobank"
      aria-labelledby="apo-partner-title"
    >
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8" aria-hidden="true" >
      <div className={styles.inner}>
        <div className={styles.head}>
          <div className={styles.logos} aria-label="ncrechner und apoBank">
            <img
              src="/images/logo-wordmark.webp"
              alt="ncrechner"
              className={styles.logoBrand}
              loading="lazy"
            />
            <span className={styles.logosX} aria-hidden="true">
              ×
            </span>
            <img
              src="/images/apobank/apobank-v1.png"
              alt="apoBank"
              className={styles.logoPartner}
              loading="lazy"
            />
          </div>
          <h2 className={`font-display ${styles.title}`} id="apo-partner-title">
            Dein Partner für Finanzen im Medizinstudium
          </h2>
          <p className={styles.text}>
            Ein weiteres Highlight für dich: unsere Kooperation mit der apoBank.
            Kostenloses Konto, Kredit fürs PJ oder Auslandsschutz für die
            Famulatur, die apoBank ist auf Medizinstudierende spezialisiert und
            bietet dir genau das, was du im Studium brauchst.
          </p>
        </div>

        <ul className={styles.boxes}>
          {boxes(placement).map((box) => (
            <li key={box.tracking}>
              <Link className={styles.box} href={box.href} data-tracking={box.tracking}>
                <span className={`font-display ${styles.boxTitle}`}>
                  {box.title}
                  <Arrow />
                </span>
                <p className={styles.boxText}>{box.text}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      </div>
    </motion.section>
  );
}
